import React, {useEffect, useRef, useState} from 'react';
import {useQuery} from '@apollo/client';
import {ArrowLeftOutlined, CameraOutlined} from '@ant-design/icons';
import {Button, Col, Drawer, Empty, Image, Input, Row, Select, Spin, Tag, message} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {normalizeLang, pickDisplayText} from '../../../../utils/i18nText';
import {
    GET_POD_RISK_REVIEW_DETAIL_BY_ID,
    GetPodRiskReviewDetailByIdResponse
} from '../graphql/queries/getPodRiskReviewDetailById';
import {
    POD_RISK_REVIEW_ACTION_ENABLED,
    PodRiskReviewAction,
    PodRiskReviewStatus,
    isPodRiskReviewFinished
} from '../graphql/types/PodRiskReviewType';
import {usePodRiskReviewActions} from '../hooks/usePodRiskReviewActions';
import type {PodRiskTypeOptionItem} from '../hooks/usePodRiskTypeOptions';

const {TextArea} = Input;

/** 审核备注长度上限，与 podReview / popReview 保持一致 */
const REMARK_MAX_LENGTH = 120;

/**
 * 审核按钮公共尺寸：宽度自适应而非写死 —— 中英西三语文案长度差异大，
 * 固定宽度会让长语种文本溢出按钮边框，故允许折行并跟着长高。
 */
const REVIEW_BUTTON_BASE = 'h-auto min-h-10 py-1.5 w-full whitespace-normal leading-tight';

/**
 * 已审单的结论按钮样式：命中当前结论的一侧加粗边框高亮，另一侧置灰。
 * 「已取消」既非通过也非驳回，两侧都不高亮。
 */
const finishedButtonClass = (highlighted: boolean, isApprove: boolean): string => {
    if (!highlighted) {
        return `${REVIEW_BUTTON_BASE} !border-gray-200 !bg-gray-100 !text-gray-400`;
    }
    return isApprove
        ? `${REVIEW_BUTTON_BASE} !border-2 !border-green-500 !bg-green-50 !text-green-600`
        : `${REVIEW_BUTTON_BASE} !border-2 !border-red-500 !bg-red-50 !text-red-500`;
};

/** 栏内的「标签：值」行；标签列定宽以保证同栏各行的值左边缘对齐 */
const LabeledText: React.FC<{label: string; value?: React.ReactNode}> = ({label, value}) => (
    <div className="flex text-sm leading-6 mb-2">
        <span className="text-gray-500 flex-shrink-0 w-[104px] mr-3">{label}:</span>
        <span className="flex-1 min-w-0 break-words">
            {value === null || value === undefined || value === '' ? '—' : value}
        </span>
    </div>
);

interface PodRiskReviewDetailDrawerProps {
    open: boolean;
    onClose: () => void;
    /**
     * 待审核单 ID 列表。
     * 长度 > 1 即批量模式：逐条展示、审完自动跳下一条（不是一次提交多条）。
     */
    reviewIds: string[];
    /** 单条审核成功后回调，供列表刷新 */
    onReviewed?: () => void;
    /** 风险类型下拉选项 */
    riskTypeOptions: PodRiskTypeOptionItem[];
    /** 风险类型码 → 当前语言文案，用于回显已选类型 */
    riskTypeTextByCode: Record<string, string>;
    /** 配送商 ID → 名称；未命中回落显示 ID */
    dspNameById: Record<string, string>;
}

const PodRiskReviewDetailDrawer: React.FC<PodRiskReviewDetailDrawerProps> = ({
    open,
    onClose,
    reviewIds,
    onReviewed,
    riskTypeOptions,
    riskTypeTextByCode,
    dspNameById
}) => {
    const {t, i18n} = useTranslation();
    const {handleReview, loading: submitting} = usePodRiskReviewActions();

    const [internalCurrentIndex, setInternalCurrentIndex] = useState(0);
    const [selectedRiskTypeCodes, setSelectedRiskTypeCodes] = useState<string[]>([]);
    const [reviewRemark, setReviewRemark] = useState('');
    // 批量审到最后一条时置位：不自动关抽屉，改为展示「没有更多了」等用户手动关
    const [showNoMore, setShowNoMore] = useState(false);
    const prevReviewIdsRef = useRef<string[]>([]);

    const isBatchMode = reviewIds.length > 1;
    const queryId = reviewIds[internalCurrentIndex];

    // 传入列表变化时重置索引，避免沿用上一批的进度
    useEffect(() => {
        const idsChanged =
            prevReviewIdsRef.current.length !== reviewIds.length ||
            !prevReviewIdsRef.current.every((id, index) => id === reviewIds[index]);
        if (idsChanged) {
            prevReviewIdsRef.current = reviewIds;
            setInternalCurrentIndex(0);
        }
    }, [reviewIds]);

    // 切换到下一条时清空上一条的审核输入
    useEffect(() => {
        setSelectedRiskTypeCodes([]);
        setReviewRemark('');
    }, [queryId]);

    // 批量模式下停留在最后一条时展示「没有更多了」；单条模式不涉及
    useEffect(() => {
        setShowNoMore(isBatchMode && internalCurrentIndex + 1 === reviewIds.length);
    }, [isBatchMode, internalCurrentIndex, reviewIds.length]);

    // 关闭后重置，避免下次打开残留上一批的「没有更多了」态
    useEffect(() => {
        if (!open) {
            setShowNoMore(false);
        }
    }, [open]);

    const {data, loading, refetch} = useQuery<GetPodRiskReviewDetailByIdResponse>(
        GET_POD_RISK_REVIEW_DETAIL_BY_ID,
        {
            variables: {reviewTaskId: queryId},
            skip: !open || !queryId,
            fetchPolicy: 'network-only'
        }
    );

    const detail = data?.getPodRiskReviewDetailById;
    const item = detail?.item;
    const finished = isPodRiskReviewFinished(item?.status);
    // 已审单用于高亮结论按钮；「已取消」两者皆 false，两侧按钮均置灰
    const approved = item?.status === PodRiskReviewStatus.APPROVED;
    const rejected = item?.status === PodRiskReviewStatus.REJECTED;

    const podImages = detail?.podImages ?? [];

    const moveToNext = () => {
        if (isBatchMode) {
            const nextIndex = internalCurrentIndex + 1;
            if (nextIndex < reviewIds.length) {
                setInternalCurrentIndex(nextIndex);
                return;
            }
            // 批量审到最后一条：不自动关，停留并展示「没有更多了」。
            // 重查当前条使其刷新为已终止只读态，避免刚审完的按钮仍可点。
            void refetch();
            return;
        }
        onClose();
    };

    const submit = async (action: PodRiskReviewAction) => {
        if (!queryId) {
            return;
        }

        // 判为「不存在风险」却勾了风险类型，二者矛盾，要求先清空再提交
        if (action === PodRiskReviewAction.APPROVE && selectedRiskTypeCodes.length > 0) {
            message.error(t('podRiskReview.messages.approveWithRiskTypesConflict'));
            return;
        }
        const ok = await handleReview(
            queryId,
            action,
            action === PodRiskReviewAction.REJECT ? selectedRiskTypeCodes : undefined,
            reviewRemark,
            // 备注语种取前端当前 locale：前端本就持有，省一次服务端语种识别
            normalizeLang(i18n.language)
        );
        if (ok) {
            onReviewed?.();
            moveToNext();
        }
    };

    // 标题即运单号；批量模式下仍需进度提示
    const title = isBatchMode
        ? t('podRiskReview.detail.titleBatch', {
              current: internalCurrentIndex + 1,
              total: reviewIds.length
          })
        : item?.trackingNumber || '';

    return (
        <Drawer
            title={
                <div className="flex items-center">
                    <ArrowLeftOutlined
                        className="mr-3 cursor-pointer hover:text-gray-600"
                        onClick={onClose}
                    />
                    <span className="text-lg font-medium">{title}</span>
                </div>
            }
            placement="right"
            // 近全屏：图片是审核的核心依据，需要足够版面
            width="calc(100vw - 80px)"
            onClose={onClose}
            open={open}
            closable={false}
            destroyOnClose
            styles={{body: {padding: 0}, header: {borderBottom: '1px solid #f0f0f0'}}}
        >
            <Spin spinning={loading}>
                {!item ? (
                    <div className="px-8 py-6">
                        <Empty description={t('podRiskReview.detail.notFound')} />
                    </div>
                ) : (
                    <div className="h-full bg-white">
                        <div className="px-6 py-2">
                            <div className="border border-gray-200 rounded-lg bg-white">
                                <div className="flex items-center px-6 py-3 border-b border-gray-100">
                                    <span className="text-gray-500 text-sm w-[104px] flex-shrink-0">
                                        {t('podRiskReview.detail.trackingLabel')}
                                    </span>
                                    <span className="text-purple-600 font-medium">
                                        {item.trackingNumber || '—'}
                                    </span>
                                </div>

                                <Row className="px-6 py-4">
                                    {/* ---------- 配送信息 ---------- */}
                                    <Col span={8} className="pr-8">
                                        <div className="mb-3 font-medium">
                                            {t('podRiskReview.detail.deliverySection')}
                                        </div>
                                        <LabeledText
                                            label={t('podRiskReview.detail.dsp')}
                                            value={
                                                item.dspId
                                                    ? dspNameById[item.dspId] || item.dspId
                                                    : undefined
                                            }
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.driver')}
                                            value={item.driverName || item.driverId}
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.warehouse')}
                                            value={item.warehouseName || item.warehouseId}
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.deliveredAt')}
                                            value={formatEpochToDateTime(item.deliveredAt)}
                                        />
                                    </Col>

                                    {/* ---------- 风控信息（淡竖线分隔） ---------- */}
                                    <Col span={8} className="px-8 border-l border-gray-100">
                                        <div className="mb-3 font-medium">
                                            {t('podRiskReview.detail.riskSection')}
                                        </div>
                                        <LabeledText
                                            label={t('podRiskReview.detail.machineHitImageCount')}
                                            value={item.machineHitImageCount}
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.machineHitTypes')}
                                            value={
                                                item.machineHitTypes?.length
                                                    ? item.machineHitTypes.join('、')
                                                    : undefined
                                            }
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.batchNo')}
                                            value={item.batchNo}
                                        />
                                    </Col>

                                    {/* ---------- 审核信息 ---------- */}
                                    <Col span={8} className="pl-8 border-l border-gray-100">
                                        <div className="mb-3 font-medium">
                                            {t('podRiskReview.detail.reviewSection')}
                                        </div>
                                        <LabeledText
                                            label={t('podRiskReview.detail.status')}
                                            value={t(
                                                `podRiskReview.enum.status.${item.status}`
                                            )}
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.reviewer')}
                                            value={item.reviewer?.name || item.reviewerId}
                                        />
                                        <LabeledText
                                            label={t('podRiskReview.detail.reviewedAt')}
                                            value={formatEpochToDateTime(item.reviewedAt)}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        {/* ---------- POD 图片 + 审核区 ---------- */}
                        <div className="px-6 py-2">
                            <Row gutter={16}>
                                <Col span={16}>
                                    <div className="border border-gray-200 rounded-lg bg-white p-4">
                                        <div className="mb-3 font-medium flex items-center">
                                            <CameraOutlined className="mr-2" />
                                            {t('podRiskReview.detail.podImages')}
                                            <span className="ml-2 text-gray-400 text-sm">
                                                ({podImages.length})
                                            </span>
                                        </div>
                                        {podImages.length === 0 ? (
                                            <Empty
                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                description={t('podRiskReview.detail.noImages')}
                                            />
                                        ) : (
                                            <Image.PreviewGroup>
                                                <div className="flex flex-wrap gap-3">
                                                    {podImages.map(img => (
                                                        <div
                                                            key={img.url}
                                                            className={`relative w-[200px] h-[200px] rounded overflow-hidden border ${img.isRisky ? 'border-red-500' : 'border-transparent'}`}
                                                        >
                                                            {img.isRisky && (
                                                                <div className="absolute top-0 left-0 right-0 z-10 bg-red-500/90 text-white text-xs font-medium text-center py-1 pointer-events-none">
                                                                    {t(
                                                                        'podRiskReview.detail.riskyImageHint'
                                                                    )}
                                                                </div>
                                                            )}
                                                            <Image
                                                                src={img.url}
                                                                width={200}
                                                                height={200}
                                                                className="object-cover"
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </Image.PreviewGroup>
                                        )}
                                    </div>
                                </Col>

                                <Col span={8}>
                                    <div className="border border-gray-200 rounded-lg bg-white p-4">
                                        <div className="mb-3 font-medium">
                                            {t('podRiskReview.detail.reviewPanel')}
                                        </div>

                                        {finished ? (
                                            <>
                                                {/* 已审单只读回显结论与类型 */}
                                                <div className="mb-3">
                                                    <div className="text-gray-500 text-sm mb-1">
                                                        {t('podRiskReview.detail.riskTypes')}
                                                    </div>
                                                    {item.riskTypeCodes?.length ? (
                                                        <div className="flex flex-wrap gap-1">
                                                            {item.riskTypeCodes.map(code => (
                                                                <Tag key={code} color="red">
                                                                    {riskTypeTextByCode[code] || code}
                                                                </Tag>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </div>
                                                <div className="mb-3">
                                                    <div className="text-gray-500 text-sm mb-1">
                                                        {t('podRiskReview.detail.remark')}
                                                    </div>
                                                    <div className="break-words">
                                                        {/* 译文可能缺失，pickDisplayText 内部回落原文 */}
                                                        {item.reviewRemark
                                                            ? pickDisplayText(
                                                                  item.reviewRemark,
                                                                  normalizeLang(i18n.language)
                                                              )
                                                            : '—'}
                                                    </div>
                                                </div>
                                                {POD_RISK_REVIEW_ACTION_ENABLED && (
                                                    <Row gutter={8}>
                                                        <Col span={12}>
                                                            <Button
                                                                disabled
                                                                className={finishedButtonClass(
                                                                    approved,
                                                                    true
                                                                )}
                                                            >
                                                                {t('podRiskReview.action.approve')}
                                                            </Button>
                                                        </Col>
                                                        <Col span={12}>
                                                            <Button
                                                                disabled
                                                                className={finishedButtonClass(
                                                                    rejected,
                                                                    false
                                                                )}
                                                            >
                                                                {t('podRiskReview.action.reject')}
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                )}
                                            </>
                                        ) : (
                                            POD_RISK_REVIEW_ACTION_ENABLED && (
                                            <>
                                                <div className="mb-3">
                                                    <div className="text-gray-500 text-sm mb-1">
                                                        {t('podRiskReview.detail.riskTypes')}
                                                    </div>
                                                    <Select
                                                        mode="multiple"
                                                        allowClear
                                                        className="w-full"
                                                        placeholder={t(
                                                            'podRiskReview.detail.riskTypesPlaceholder'
                                                        )}
                                                        value={selectedRiskTypeCodes}
                                                        onChange={setSelectedRiskTypeCodes}
                                                        options={riskTypeOptions}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <div className="text-gray-500 text-sm mb-1">
                                                        {t('podRiskReview.detail.remark')}
                                                    </div>
                                                    <TextArea
                                                        rows={3}
                                                        maxLength={REMARK_MAX_LENGTH}
                                                        showCount
                                                        value={reviewRemark}
                                                        onChange={e =>
                                                            setReviewRemark(e.target.value)
                                                        }
                                                        placeholder={
                                                            t(
                                                                'podRiskReview.detail.remarkPlaceholder'
                                                            ) as string
                                                        }
                                                    />
                                                </div>
                                                <Row gutter={8}>
                                                    <Col span={12}>
                                                        <Button
                                                            loading={submitting}
                                                            className={REVIEW_BUTTON_BASE}
                                                            onClick={() =>
                                                                submit(PodRiskReviewAction.APPROVE)
                                                            }
                                                        >
                                                            {t('podRiskReview.action.approve')}
                                                        </Button>
                                                    </Col>
                                                    <Col span={12}>
                                                        <Button
                                                            danger
                                                            type="primary"
                                                            loading={submitting}
                                                            className={REVIEW_BUTTON_BASE}
                                                            onClick={() =>
                                                                submit(PodRiskReviewAction.REJECT)
                                                            }
                                                        >
                                                            {t('podRiskReview.action.reject')}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </>
                                            )
                                        )}

                                        {showNoMore && (
                                            <div className="mt-3 text-center text-gray-400 text-sm">
                                                {t('podRiskReview.detail.noMore')}
                                            </div>
                                        )}
                                    </div>

                                    {/* ---------- 操作日志 ---------- */}
                                    {!!detail?.logs?.length && (
                                        <div className="border border-gray-200 rounded-lg bg-white p-4 mt-4">
                                            <div className="mb-3 font-medium">
                                                {t('podRiskReview.detail.logs')}
                                            </div>
                                            {detail.logs.map(log => (
                                                <div
                                                    key={log.id || `${log.action}-${log.createdAt}`}
                                                    className="text-sm leading-6 mb-1 last:mb-0"
                                                >
                                                    <span className="text-gray-400 mr-2">
                                                        {formatEpochToDateTime(log.createdAt)}
                                                    </span>
                                                    <span className="mr-2">
                                                        {log.operatorId || '—'}
                                                    </span>
                                                    <span>{log.action}</span>
                                                    {log.comment && (
                                                        <span className="ml-2 text-gray-500">
                                                            {log.comment}
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </div>
                    </div>
                )}
            </Spin>
        </Drawer>
    );
};

export default PodRiskReviewDetailDrawer;
